import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface DescriptionsItemType {
	key?: string;
	label: React.ReactNode;
	children: React.ReactNode;
	span?: number;
	labelStyle?: React.CSSProperties;
	contentStyle?: React.CSSProperties;
}

export interface DescriptionsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
	items?: DescriptionsItemType[];
	title?: React.ReactNode;
	extra?: React.ReactNode;
	bordered?: boolean;
	column?:
		| number
		| {
				xs?: number;
				sm?: number;
				md?: number;
				lg?: number;
				xl?: number;
				xxl?: number;
		  };
	size?: "default" | "middle" | "small";
	layout?: "horizontal" | "vertical";
	colon?: boolean;
	labelStyle?: React.CSSProperties;
	contentStyle?: React.CSSProperties;
}

const Descriptions = forwardRef<HTMLDivElement, DescriptionsProps>(
	(
		{
			className,
			items = [],
			title,
			extra,
			bordered = false,
			column = 3,
			size = "default",
			layout = "horizontal",
			colon = true,
			labelStyle,
			contentStyle,
			...props
		},
		ref,
	) => {
		const getColumn = (): number => {
			if (typeof column === "number") {
				return column;
			}
			// Simple responsive logic (could be enhanced with window width detection)
			return column.md ?? column.sm ?? column.xs ?? 3;
		};

		const cols = getColumn();

		const sizeStyles = {
			default: {
				padding: bordered ? "px-4 py-3" : "pb-4",
				labelPadding: bordered ? "px-4 py-3" : "pb-4 pr-4",
			},
			middle: {
				padding: bordered ? "px-3 py-2" : "pb-3",
				labelPadding: bordered ? "px-3 py-2" : "pb-3 pr-3",
			},
			small: {
				padding: bordered ? "px-2 py-1.5" : "pb-2",
				labelPadding: bordered ? "px-2 py-1.5" : "pb-2 pr-2",
			},
		};

		// Group items into rows based on column count and span
		const rows: DescriptionsItemType[][] = [];
		let currentRow: DescriptionsItemType[] = [];
		let currentSpan = 0;

		items.forEach((item) => {
			const itemSpan = item.span ?? 1;
			if (currentSpan + itemSpan > cols) {
				if (currentRow.length > 0) {
					rows.push(currentRow);
				}
				currentRow = [item];
				currentSpan = itemSpan;
			} else {
				currentRow.push(item);
				currentSpan += itemSpan;
			}
		});

		if (currentRow.length > 0) {
			rows.push(currentRow);
		}

		if (bordered) {
			return (
				<div ref={ref} className={cn("w-full", className)} {...props}>
					{(title || extra) && (
						<div className="flex items-center justify-between mb-4">
							{title && (
								<h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
									{title}
								</h3>
							)}
							{extra && <div>{extra}</div>}
						</div>
					)}
					<table className="w-full border-collapse border border-gray-200 dark:border-gray-700">
						<tbody>
							{rows.map((row, rowIndex) => (
								<tr key={rowIndex}>
									{row.map((item, itemIndex) => {
										const span = item.span ?? 1;
										const itemKey = item.key ?? `item-${itemIndex}`;
										if (layout === "vertical") {
											return (
												<td
													key={itemKey}
													colSpan={span * 2}
													className="border border-gray-200 dark:border-gray-700"
												>
													<div
														className={cn(
															"bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-sm font-medium",
															sizeStyles[size].labelPadding,
														)}
														style={{ ...labelStyle, ...item.labelStyle }}
													>
														{item.label}
														{colon && ":"}
													</div>
													<div
														className={cn(
															"text-gray-700 dark:text-gray-200",
															sizeStyles[size].padding,
														)}
														style={{ ...contentStyle, ...item.contentStyle }}
													>
														{item.children}
													</div>
												</td>
											);
										}
										// Calculate colspan: if span > 1, it takes more columns
										// Each item takes 2 cells (th + td), so span of 2 means colSpan of (2*2-1)=3 for td
										const colSpan = span > 1 ? span * 2 - 1 : 1;
										return (
											<React.Fragment key={itemKey}>
												<th
													className={cn(
														"border border-gray-200 dark:border-gray-700 text-left font-medium",
														"bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-sm",
														"whitespace-nowrap",
														sizeStyles[size].labelPadding,
													)}
													style={{ ...labelStyle, ...item.labelStyle }}
												>
													{item.label}
													{colon && ":"}
												</th>
												<td
													colSpan={colSpan}
													className={cn(
														"border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200",
														sizeStyles[size].padding,
													)}
													style={{ ...contentStyle, ...item.contentStyle }}
												>
													{item.children}
												</td>
											</React.Fragment>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			);
		}

		// Non-bordered layout
		return (
			<div ref={ref} className={cn("w-full", className)} {...props}>
				{(title || extra) && (
					<div className="flex items-center justify-between mb-4">
						{title && (
							<h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
						)}
						{extra && <div>{extra}</div>}
					</div>
				)}
				<div
					className={cn("grid gap-x-4", layout === "vertical" && "gap-y-0")}
					style={{
						gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
					}}
				>
					{items.map((item, index) => {
						const span = item.span ?? 1;
						return (
							<div
								key={item.key ?? index}
								className={cn(
									layout === "horizontal" && "flex",
									"border-b border-gray-100 dark:border-gray-800",
								)}
								style={{
									gridColumn: `span ${Math.min(span, cols)}`,
								}}
							>
								{layout === "horizontal" ? (
									<>
										<span
											className={cn(
												"flex-shrink-0 text-gray-500 dark:text-gray-400 text-sm",
												sizeStyles[size].labelPadding,
											)}
											style={{ ...labelStyle, ...item.labelStyle }}
										>
											{item.label}
											{colon && ":"}
										</span>
										<span
											className={cn(
												"flex-1 text-gray-700 dark:text-gray-200",
												sizeStyles[size].padding,
											)}
											style={{ ...contentStyle, ...item.contentStyle }}
										>
											{item.children}
										</span>
									</>
								) : (
									<>
										<div
											className={cn(
												"text-gray-500 dark:text-gray-400 text-sm pt-3",
												sizeStyles[size].labelPadding,
											)}
											style={{ ...labelStyle, ...item.labelStyle }}
										>
											{item.label}
											{colon && ":"}
										</div>
										<div
											className={cn("text-gray-700 dark:text-gray-200", sizeStyles[size].padding)}
											style={{ ...contentStyle, ...item.contentStyle }}
										>
											{item.children}
										</div>
									</>
								)}
							</div>
						);
					})}
				</div>
			</div>
		);
	},
);

Descriptions.displayName = "Descriptions";

// Descriptions.Item for backward compatibility
// Note: Prefer using items prop instead
interface DescriptionsItemProps {
	label: React.ReactNode;
	span?: number;
	labelStyle?: React.CSSProperties;
	contentStyle?: React.CSSProperties;
	children?: React.ReactNode;
}

const DescriptionsItem: React.FC<DescriptionsItemProps> = ({ children }) => {
	// This is a marker component - actual rendering happens in Descriptions
	return <>{children}</>;
};

DescriptionsItem.displayName = "Descriptions.Item";

// Compound component
interface DescriptionsComponent extends React.ForwardRefExoticComponent<
	DescriptionsProps & React.RefAttributes<HTMLDivElement>
> {
	Item: typeof DescriptionsItem;
}

const DescriptionsWithCompound = Descriptions as DescriptionsComponent;
DescriptionsWithCompound.Item = DescriptionsItem;

export { DescriptionsWithCompound as Descriptions, DescriptionsItem };
